import React, { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';

/**
 * Custom modal for assign orders workflow
 * States: confirm -> progress -> success
 */
const AssignOrdersModal = ({
  isOpen,
  onClose,
  onConfirm,
  orderCount,
  assigneeName,
  state = 'confirm', // 'confirm' | 'progress' | 'success'
}) => {
  // Auto-close on success after 2.5 seconds
  useEffect(() => {
    if (state === 'success') {
      const timer = setTimeout(() => {
        onClose();
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [state, onClose]);

  const renderContent = () => {
    switch (state) {
      case 'confirm':
        return (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Assign Order(s)</DialogTitle>
              <DialogDescription className="text-base mt-2">
                {orderCount === 1
                  ? `Assign this order to ${assigneeName}?`
                  : `Assign ${orderCount} order(s) to ${assigneeName}?`}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-6 gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="default" onClick={onConfirm} className="bg-blue-600 hover:bg-blue-700">
                Confirm
              </Button>
            </DialogFooter>
          </>
        );

      case 'progress':
        return (
          <div className="py-8 px-4 text-center">
            <div className="mb-4">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
            </div>
            <p className="text-lg font-medium text-gray-700 mb-2">Assigning order(s)...</p>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-4 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 h-full rounded-full animate-progress-bar"></div>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="py-8 px-4 text-center">
            <div className="mb-4 flex justify-center">
              <div className="rounded-full bg-green-100 p-3">
                <svg
                  className="h-12 w-12 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
            <p className="text-lg font-semibold text-gray-800 mb-2">
              Operation completed successfully!
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-4 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 h-full rounded-full w-full transition-all duration-500"></div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && state !== 'progress' && onClose()}>
      <DialogContent className="sm:max-w-md">
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default AssignOrdersModal;
